import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import { useCreateProjectComment } from 'src/Hooks/Mutations/useCreateProjectComment';
import { useDeleteProjectComment } from 'src/Hooks/Mutations/useDeleteProjectComment';
import { useEditProjectComment } from 'src/Hooks/Mutations/useEditProjectComment';
import useFetchProjectComments from 'src/Hooks/Queries/useFetchProjectComments';

import { CommentsList } from '../CommentsList';

export type ProjectCommentsProps = {
  projectId: string;
  assetId: string;
  highlightedCommentId?: string;
};

const ProjectComments = ({ projectId, assetId, highlightedCommentId }: ProjectCommentsProps) => {
  const { t } = useTranslation();

  const {
    data: infiniteComments,
    isFetchingNextPage,
    isLoading,
  } = useFetchProjectComments(projectId, assetId);

  const { mutate: createComment, isLoading: isCreateLoading } = useCreateProjectComment({
    onSuccess() {
      notifications.show({
        message: t('community.comments.createSuccess', 'Votre commentaire a été publié.'),
      });
    },
  });

  const { mutate: editComment, isLoading: isEditLoading } = useEditProjectComment({
    onSuccess() {
      notifications.show({
        message: t('community.comments.edited', 'Votre commentaire est modifié.'),
      });
    },
  });

  const { mutate: deleteComment } = useDeleteProjectComment({
    onSuccess() {
      notifications.show({
        message: t('community.comments.deleted', 'Votre commentaire est supprimé.'),
      });
    },
  });

  const comments = infiniteComments?.pages.flatMap(page => page.items) ?? [];

  const handleCreateComment = (message: string) => {
    createComment({ projectId, assetId, message });
  };

  const handleEditComment = (commentId: string, message: string) => {
    editComment({ projectId, assetId, commentId, message });
  };

  const handleDeleteComment = (commentId: string) => {
    deleteComment({ projectId, assetId, commentId });
  };

  return (
    <CommentsList
      comments={comments}
      isLoading={isLoading}
      isFetchingMore={isFetchingNextPage}
      highlightedCommentId={highlightedCommentId}
      onCreateComment={handleCreateComment}
      onEditComment={handleEditComment}
      onDeleteComment={handleDeleteComment}
      isCreateLoading={isCreateLoading}
      isEditLoading={isEditLoading}
    />
  );
};

export default ProjectComments;
